/**
 * featureFlags — single source of truth for MVP feature toggles.
 *
 * Flags are read at build time from REACT_APP_ENABLE_* env vars (CRA inlines them).
 * Defaults are intentionally conservative: every non-MVP feature is OFF unless
 * explicitly enabled in the environment.
 *
 * THE RULE — fails CLOSED, and it is the server's rule, not a client dialect:
 *   unset or empty  → the flag's declared fallback (every one of them is false)
 *   otherwise       → trim, lowercase, and open ONLY for "true" or "1"
 * Anything else — "off", "no", "disabled", "FALSE", "yes", "maybe", a typo —
 * leaves the flag closed. This is copied from server/middleware/featureGate.js
 * (isFlagEnabled) so the two genuinely cannot drift; featureFlags.test.js pins
 * the whole table against that server predicate, case for case.
 *
 * It used to be a denylist (`v !== "false" && v !== "0"`), which opened the UI
 * on "FALSE", "off", "no", "disabled", "false " and " 0" while the server kept
 * refusing them (#165) — every one of those a falsy INTENT rendered as ON.
 *
 * Usage:
 *   import { FEATURE_FLAGS } from "../utils/featureFlags";
 *   if (FEATURE_FLAGS.ENABLE_GLOBAL_MARKETPLACE) { ... }
 */

const flag = (name, fallback) => {
  const raw = process.env[name];
  if (raw === undefined || raw === "") return fallback;
  if (typeof raw !== "string") return false;
  const normalized = raw.trim().toLowerCase();
  return normalized === "true" || normalized === "1";
};

const isProductionBuild = process.env.NODE_ENV === "production";

export const FEATURE_FLAGS = {
  // Posts/friends/decks UI. Backend models stay so we can re-enable later.
  ENABLE_SOCIAL: flag("REACT_APP_ENABLE_SOCIAL", false),

  // Bid placement / auction UX. The single client-side switch for the bidding
  // UI (#84) — but visibility only: the server gates /bids and /cart
  // fail-closed behind its own ENABLE_BIDDING / ENABLE_CART env vars, so
  // flipping this alone exposes nothing. There is no client cart flag because
  // no cart UI is mounted (see docs/CART_DEPENDENCIES.md).
  ENABLE_BIDDING: flag("REACT_APP_ENABLE_BIDDING", false),

  // Worldwide region chips on the marketplace. MVP is Israel-first.
  ENABLE_GLOBAL_MARKETPLACE: flag("REACT_APP_ENABLE_GLOBAL_MARKETPLACE", false),

  // City-search roaming: browse collectors in other cities (remote trading).
  // Off for beta — local-only discovery.
  ENABLE_ROAMING: flag("REACT_APP_ENABLE_ROAMING", false),

  // Synthetic collectors on the map when no real users are returned.
  // Default: OFF in production, opt-in in dev only.
  ENABLE_MOCK_USERS: !isProductionBuild && flag("REACT_APP_ENABLE_MOCK_USERS", false),
};

export default FEATURE_FLAGS;
